/** A tiny toast store; <Toaster/> renders it. */
import { useSyncExternalStore } from 'react';

export interface Toast {
  id: number;
  kind: 'success' | 'error' | 'info';
  message: string;
  action?: { label: string; href: string };
  /** Toasts with the same key replace each other instead of stacking. */
  key?: string;
}

let toasts: Toast[] = [];
let seq = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function push(kind: Toast['kind'], message: string, extra: Pick<Toast, 'action' | 'key'> = {}) {
  const id = ++seq;
  const rest = extra.key ? toasts.filter((t) => t.key !== extra.key) : toasts;
  toasts = [...rest.slice(-3), { id, kind, message, ...extra }];
  emit();
  setTimeout(() => dismiss(id), kind === 'error' ? 7000 : 3500);
}

export function dismiss(id: number) {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

export const toast = {
  success: (m: string) => push('success', m),
  error: (m: string) => push('error', m),
  info: (m: string) => push('info', m),
  /** A write refused because the trial or subscription has lapsed: points to the Billing tab. */
  paywall: (m: string) => push('error', m, { key: 'paywall', action: { label: 'Subscribe', href: '/settings?tab=billing' } }),
};

export function useToasts() {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => toasts,
    // None on the server, which renders the public pages (ssr.tsx).
    () => none,
  );
}

const none: Toast[] = [];
